import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { formatAge, groupRows, topicKey, type Scope } from "@vladrss/core";
import { Icon } from "./Icon";
import { readingLabel, useArticleActions, useCounts, useFeeds, useListRows, type Feed } from "../lib/library";
import { setPrefs, toggleNav, usePrefs, type Density } from "../lib/prefs";
import { api, useOwnerQuery } from "../lib/api";
import { onLeave, placeAt, type Placing } from "../lib/kept";
import { visibleRows, getState, memorize, openSettings, recall, setView, update, useStore, viewKey, type Row, peekAt, clearPicks, pick, pickAll, rowCache, withOverlay } from "../lib/store";

export function FeedIcon({ feed, size = 16 }: { feed?: Pick<Feed, "iconUrl" | "title" | "failingSince"> | null; size?: number }) {
  const [broken, setBroken] = useState(false);
  if (feed?.failingSince) return <span className="fi bad" style={{ width: size, height: size }} title="This source is failing"><Icon name="alert" size={size - 4} /></span>;
  const letter = (feed?.title ?? "?").replace(/^the\s+/i, "").charAt(0).toUpperCase();
  if (!feed?.iconUrl || broken) return <span className="fi mono" style={{ width: size, height: size, fontSize: size * 0.58 }} aria-hidden="true">{letter}</span>;
  return <img className="fi" src={feed.iconUrl} width={size} height={size} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} />;
}

const KIND_TAG: Record<string, string> = { comic: "Comic", trail: "Trail", video: "Video" };
/** A video post's title without "Video:" in front: its tag says so. */
const shownTitle = (row: Row) => (row.video ? row.title.replace(/^video\s*[:|–-]\s*/i, "") : row.title);

/** Ctrl/⌘ or Shift picks rows; a plain click opens one (and lets go of any picks). */
function clickRow(e: React.MouseEvent, id: string, open: (id: string) => void) {
  if (e.ctrlKey || e.metaKey || e.shiftKey) { pick(id, e.shiftKey); return; }
  clearPicks();
  open(id);
}

/** In a topic's list, what a row says under its title: the passage that names the topic, marked, when the title does not. */
function Excerpt({ row, topic }: { row: Row; topic?: string }): ReactNode {
  const m = topic ? row.topicMatches?.find((x) => x.topic === topic) : undefined;
  if (!m) return row.preview;
  return <>{m.text.slice(0, m.start)}<mark>{m.text.slice(m.start, m.end)}</mark>{m.text.slice(m.end)}</>;
}

export const ArticleRow = memo(function ArticleRow({ row, feed, density, selected, onOpen, showSource = true, topic }: {
  row: Row; feed?: Feed; density: Density; selected: boolean; onOpen: (id: string) => void; showSource?: boolean;
  /** the key of the topic whose list this is */
  topic?: string;
}) {
  const { read, save } = useArticleActions();
  const picked = useStore((st) => st.picks.picked.has(row.id));
  const [broken, setBroken] = useState(false); // a photo that would not load counts as none
  const photo = row.imageUrl && !broken ? row.imageUrl : null;
  const tag = KIND_TAG[row.kind] ?? (row.video ? "Video" : undefined);
  const title = shownTitle(row);
  const age = formatAge(row.publishedAt, Date.now(), new Date().getTimezoneOffset());
  const cls = `r d-${density}${showSource ? "" : " nosrc"}${row.isRead ? " is-read" : ""}${row.isSaved ? " is-saved" : ""}${selected ? " is-sel" : ""}${picked ? " is-picked" : ""}${photo ? "" : " noimg"}`;
  const src = feed?.title ?? "";
  const acts = (
    <div className="acts" onClick={(e) => e.stopPropagation()}>
      <button className="ib sm" onClick={() => read(row.id, !row.isRead)} aria-label={row.isRead ? "Mark unread" : "Mark read"} data-tip={row.isRead ? "Mark unread" : "Mark read"}><Icon name={row.isRead ? "circleDot" : "check"} size={16} /></button>
      <button className="ib sm" onClick={() => save(row.id, !row.isSaved)} aria-pressed={row.isSaved} aria-label="Save" data-tip={row.isSaved ? "Saved" : "Save"}><Icon name={row.isSaved ? "bookmarkF" : "bookmark"} size={16} /></button>
      <a className="ib sm" href={row.url} target="_blank" rel="noopener noreferrer" aria-label="Open original" data-tip="Open original"><Icon name="ext" size={16} /></a>
    </div>
  );
  const thumb = photo ? (
    <div className={`tn${row.kind === "comic" ? " comic" : ""}`}>
      {row.video && <span className="play" aria-hidden="true" />}
      <img src={photo} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" onLoad={(e) => e.currentTarget.classList.add("ld")} onError={() => setBroken(true)} />
    </div>
  ) : (
    // no photo: where photos lead the row (the wide list), its source's mark keeps the rows in line
    <div className="tn ph" aria-hidden="true"><FeedIcon feed={feed} size={26} /></div>
  );
  // within one source the source name would repeat on every row; the age is enough
  const meta = showSource
    ? <div className="meta"><FeedIcon feed={feed} /><span className="src">{src}</span><span aria-hidden="true">·</span><span className="age" title={new Date(row.publishedAt).toLocaleString()}>{age}</span></div>
    : <div className="meta"><span className="age" title={new Date(row.publishedAt).toLocaleString()}>{age}</span></div>;
  return (
    <div className={cls} data-id={row.id} role="option" aria-selected={selected || picked} onClick={(e) => clickRow(e, row.id, onOpen)} onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }} onMouseEnter={() => peekAt(row.id)}>
      {meta}
      <div className="main"><h3 className="ttl">{tag && <span className="tag">{tag}</span>}{title}</h3>{(row.preview || topic) && <p className="ex"><Excerpt row={row} topic={topic} /></p>}</div>
      {density === "previews" && thumb}
      {acts}
    </div>
  );
});

export function ListPane({ onOpen, onStart, rowsOut }: { onOpen: (id: string) => void; onStart: () => void; rowsOut: (rows: Row[], pageRevision: number, collectionKey: string) => void }) {
  const prefs = usePrefs();
  const scope = useStore((st) => st.scope);
  const filter = useStore((st) => st.filter);
  const reading = useStore((st) => st.reading);
  const selected = useStore((st) => st.selected);
  const feeds = useFeeds();
  const { rows, canLoadMore, loadMore, complete, loadingMore, pageRevision, key: collectionKey } = useListRows();
  const { markAll } = useArticleActions();
  const { forScope } = useCounts();
  const [menu, setMenu] = useState<null | "view" | "more">(null);
  const scroller = useRef<HTMLDivElement>(null);
  const feedMap = useMemo(() => new Map<string, Feed>((feeds ?? []).map((f) => [f.id, f])), [feeds]);
  const label = readingLabel(scope, feeds);
  const density = prefs.density;
  const key = viewKey();

  useEffect(() => { if (rows) { visibleRows(rows.map((r) => r.id)); rowsOut(rows, pageRevision, collectionKey); } }, [rows, rowsOut, pageRevision, collectionKey]);

  // restore this view's scroll position and selection once its rows are in
  const restored = useRef<string | null>(null);
  const placing = useRef<Placing | null>(null);
  useLayoutEffect(() => {
    if (!rows || restored.current === key || !scroller.current) return;
    restored.current = key;
    const m = recall(key);
    placing.current?.stop();
    // after a refresh the rows above may differ (read ones are gone): back to the same row, not the same pixel
    const el = scroller.current;
    const anchor = m?.anchor ? el.querySelector<HTMLElement>(`[data-id="${CSS.escape(m.anchor)}"]`) : null;
    const top = anchor ? el.scrollTop + anchor.getBoundingClientRect().top - el.getBoundingClientRect().top + (m!.offset ?? 0) : m?.top ?? 0;
    placing.current = placeAt(el, top);
    if (m?.selected && rows.some((r) => r.id === m.selected)) update({ selected: m.selected });
  }, [rows, key]);
  useEffect(() => () => placing.current?.stop(), []);
  // the view chosen again (store.setView): back to its top
  useEffect(() => {
    const top = () => { placing.current?.stop(); scroller.current?.scrollTo({ top: 0, behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" }); };
    addEventListener("vladrss:list-top", top);
    return () => removeEventListener("vladrss:list-top", top);
  }, []);
  // as the page goes away, which row is at the top of the list (for the refresh above)
  const keyNow = useRef(key);
  keyNow.current = key;
  useEffect(() => onLeave(() => {
    const el = scroller.current;
    if (!el) return;
    const t = el.getBoundingClientRect().top;
    const row = Array.from(el.querySelectorAll<HTMLElement>("[data-id]")).find((r) => r.getBoundingClientRect().bottom > t + 1);
    const m = recall(keyNow.current);
    if (row) memorize(keyNow.current, el.scrollTop, m?.selected ?? null, row.dataset.id, t - row.getBoundingClientRect().top);
  }), []);

  // Opening/closing owns its scroll anchor in asTransition. Only navigation within
  // an unchanged layout should start a follow-selection scroll.
  const wasReading = useRef(!!reading);
  useLayoutEffect(() => {
    const changedLayout = wasReading.current !== !!reading;
    wasReading.current = !!reading;
    if (changedLayout) { placing.current?.stop(); return; }
    const id = reading?.id ?? selected;
    if (!id || !scroller.current) return;
    const el = scroller.current.querySelector<HTMLElement>(`[data-id="${CSS.escape(id)}"]`);
    if (!el) return;
    const r = el.getBoundingClientRect(), b = scroller.current.getBoundingClientRect();
    if (r.top < b.top + 40 || r.bottom > b.bottom - 24) scroller.current.scrollTo({ top: scroller.current.scrollTop + (r.top - b.top) - scroller.current.clientHeight * 0.3, behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  }, [reading?.id, selected]);

  const groups = useMemo(() => rows ? groupRows(rows, scope.type === "recent" ? "recent" : scope.type === "feed" || scope.type === "search" ? "time" : prefs.group, Date.now(), new Date().getTimezoneOffset(), (id) => feedMap.get(id)?.title ?? "") : [], [rows, scope.type, prefs.group, feedMap]);
  const byId = useMemo(() => new Map((rows ?? []).map((r) => [r.id, r])), [rows]);
  const firstUnread = rows?.find((r) => !r.isRead);
  const groupable = scope.type === "all" || scope.type === "category" || scope.type === "topic";
  const counts = forScope(scope);
  const showFilter = scope.type !== "recent" && scope.type !== "search";
  const canMark = filter !== "saved" && showFilter && counts.unread > 0;
  const meta = scope.type === "recent" ? "Most recently read first"
    : filter === "saved" ? `${scope.type === "topic" ? rows?.length ?? 0 : counts.saved} saved`
    : filter === "all" ? `${counts.total} articles · ${counts.unread} unread`
    : `${counts.unread} unread`;
  const startable = !reading && scope.type !== "recent" && filter !== "saved" && !!firstUnread;
  // the floating "Up next" is only worth showing while the next unread row is out of sight
  const [nextInView, setNextInView] = useState(true);
  useEffect(() => {
    const el = firstUnread && scroller.current?.querySelector<HTMLElement>(`[data-id="${CSS.escape(firstUnread.id)}"]`);
    if (!el) { setNextInView(false); return; }
    const io = new IntersectionObserver(([e]) => setNextInView(!!e?.isIntersecting), { root: scroller.current, threshold: 0.6 });
    io.observe(el);
    return () => io.disconnect();
  }, [firstUnread?.id, rows]);

  return (
    <section className="lcol" aria-label="Articles">
      <header className="lbar">
        <button className="ib navbtn" onClick={toggleNav} aria-label="Toggle sources" data-tip="Sources" data-key="["><Icon name="sidebar" /></button>
        <div className="lt">
          <h1>{filter === "saved" && scope.type === "all" ? "Saved" : label}</h1>
          <span className="lmeta">{meta}</span>
          {filter === "all" && showFilter && (
            <button className="rchip" onClick={() => setView(scope, "unread")} aria-label="Read articles are shown. Hide them" data-tip="Back to unread only">Read shown<Icon name="x" size={12} /></button>
          )}
          {scope.type === "search" && <button className="ib sm" onClick={() => setView({ type: "all" })} aria-label="Clear search" data-tip="Clear search"><Icon name="x" size={16} /></button>}
        </div>
        <div className="lbar-r">
          {scope.type !== "recent" && (
            <button className="qa order" onClick={() => setPrefs({ order: prefs.order === "oldest" ? "newest" : "oldest" })} aria-label={`${prefs.order === "oldest" ? "Oldest" : "Newest"} first. Switch to ${prefs.order === "oldest" ? "newest" : "oldest"} first`} data-tip={`Read ${prefs.order === "oldest" ? "newest" : "oldest"} first instead`} data-key="⇧ O">
              <Icon name={prefs.order === "oldest" ? "arrowU" : "arrowD"} size={16} /><span className="qa-l swap"><span aria-hidden={prefs.order !== "oldest"}>Oldest</span><span aria-hidden={prefs.order === "oldest"}>Newest</span></span>
            </button>
          )}
          {startable && (
            <button className="qa start" onClick={onStart} aria-label={`Start reading: ${firstUnread!.title}`} data-tip="Start reading" data-key="J">
              <Icon name="play" size={14} /><span className="qa-l">Start reading</span>
            </button>
          )}
          {/* where the sources are not on screen (a drawer, a folded rail) the search is one tap away, as on the phone */}
          <button className="qa find" onClick={() => update({ modal: "palette" })} aria-label="Search or jump to" data-tip="Search or jump to" data-key={navigator.platform.includes("Mac") ? "⌘ K" : "Ctrl K"}><Icon name="search" size={16} /></button>
          {canMark && (
            <button className="qa markall" onClick={() => markAll(label)} aria-label={`Mark all ${counts.unread} read`} data-tip={`Mark all ${counts.unread} in ${label} read`} data-key="⇧ A">
              <Icon name="checks" size={16} />
            </button>
          )}
          <div className="more-wrap m-more">
            <button className={`qa${menu ? " open" : ""}`} onClick={() => setMenu(menu ? null : "more")} aria-label="View and more" aria-haspopup="menu" aria-expanded={!!menu} data-tip="View and more"><Icon name="more" size={18} /></button>
            {menu && <ListMenu close={() => setMenu(null)} groupable={groupable} showRead={showFilter && filter !== "saved"} markAll={canMark ? { n: counts.unread, run: () => markAll(label) } : undefined} />}
          </div>
          {/* beside an open article, the list folds away as the sources do; the reader bar brings it back */}
          {reading && <button className="ib listbtn" onClick={() => setPrefs({ listOff: true })} aria-label="Hide the list" data-tip="Hide the list" data-key={"\\"}><Icon name="listpane" /></button>}
        </div>
      </header>
      {startable && (
        <button className={`upnext${nextInView ? " near" : ""}`} onClick={onStart} aria-label={`Start reading: ${firstUnread.title}`} data-key="J">
          <span className="up-play"><Icon name="play" size={14} /></span>
          <span className="up-t"><span className="up-l"><FeedIcon feed={feedMap.get(firstUnread.feedId)} size={12} />Up next · {feedMap.get(firstUnread.feedId)?.title}</span><span className="up-h">{firstUnread.title}</span></span>
          <span className="up-r">Start reading<kbd>J</kbd></span>
        </button>
      )}
      <PickBar shown={rows?.length ?? 0} />
      <div className={`lscroll d-${density}`} ref={scroller} tabIndex={-1} role="listbox" aria-label={label}
        onScroll={(e) => { if (!placing.current?.mine(e.currentTarget.scrollTop)) memorize(key, e.currentTarget.scrollTop, getState().selected); if (canLoadMore && e.currentTarget.scrollTop + e.currentTarget.clientHeight > e.currentTarget.scrollHeight - 600) loadMore(); }}>
        {!rows ? <Skeleton /> : rows.length === 0 ? <Empty scope={scope} label={label} /> : (
          <>
            {groups.map((g) => (
              <div className="grp" key={g.key}>
                <div className={`gh${g.feedId ? " src" : ""}`}>
                  {g.feedId ? <><FeedIcon feed={feedMap.get(g.feedId)} /><button onClick={() => setView({ type: "feed", feedId: g.feedId! })}>{g.label}</button></> : <span>{g.label}</span>}
                </div>
                <div>
                  {g.ids.map((id) => {
                    const r = byId.get(id)!;
                    return <ArticleRow key={id} row={r} feed={feedMap.get(r.feedId)} density={density} selected={(reading?.id ?? selected) === id} onOpen={onOpen} showSource={scope.type !== "feed"} topic={scope.type === "topic" ? topicKey(scope.name) : undefined} />;
                  })}
                </div>
              </div>
            ))}
            {(canLoadMore || loadingMore) && <div className="eol"><button className="btn" disabled={loadingMore} onClick={loadMore}>{loadingMore ? "Loading…" : "Load more articles"}</button></div>}
            {complete && scope.type !== "recent" && scope.type !== "search" && (
              <div className="eol">
                <b>{filter === "unread" && !counts.unread ? `All caught up in ${label}` : "End of the list"}</b>
                {canMark && <button className="btn eol-mark" onClick={() => markAll(label)} data-key="⇧ A"><Icon name="checks" size={16} />Mark all read</button>}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}


/** Closes a popover when the pointer goes down anywhere outside its own wrapper. */
function useAway(cls: string, close: () => void) {
  useEffect(() => { const h = (e: MouseEvent) => { if (!(e.target as Element).closest(cls)) close(); }; addEventListener("pointerdown", h); return () => removeEventListener("pointerdown", h); }, [cls, close]);
}

/** View and more: every setting is one click, nothing opens a second level. */
function ListMenu({ close, groupable, showRead, markAll }: { close: () => void; groupable: boolean; showRead: boolean; markAll?: { n: number; run: () => void } }) {
  useAway(".m-more", close);
  const prefs = usePrefs();
  const filter = useStore((st) => st.filter);
  const scope = useStore((st) => st.scope);
  const sw = (label: string, icon: string, on: boolean, set: (v: boolean) => void, key?: string) => (
    <label className="mi mi-sw"><Icon name={icon} size={16} /><span>{label}</span>{key && <kbd>{key}</kbd>}<input type="checkbox" role="switch" className="switch" checked={on} onChange={(e) => set(e.target.checked)} /></label>
  );
  return (
    <div className="menu lmenu" role="menu" aria-label="View and more">
      <span className="caps mh">Show as</span>
      <div className="dseg" role="radiogroup" aria-label="Show as">
        {(["compact", "previews"] as const).map((d, i) => (
          <button key={d} className={prefs.density === d ? "on" : ""} role="radio" aria-checked={prefs.density === d} onClick={() => setPrefs({ density: d })} data-key={String(i + 1)}>
            <Icon name={d} size={18} />{d[0]!.toUpperCase() + d.slice(1)}
          </button>
        ))}
      </div>
      {prefs.density === "previews" && sw("Long previews", "previews", prefs.pv === "long", (v) => setPrefs({ pv: v ? "long" : "short" }))}
      {groupable && sw("Group by source", "stack", prefs.group === "source", (v) => setPrefs({ group: v ? "source" : "time" }))}
      {showRead && sw("Show read articles", "eye", filter === "all", (v) => setView(scope, v ? "all" : "unread"))}
      {/* on a phone the header has no room for it, so it is here, as on the Android app */}
      {markAll && <button className="mi mark-only" role="menuitem" onClick={() => { close(); markAll.run(); }}><Icon name="checks" size={16} />Mark all {markAll.n} read</button>}
      <div className="menu-sep" />
      <button className="mi" role="menuitem" onClick={() => { close(); openSettings("sources"); }}><Icon name="sliders" size={16} />Settings<kbd>,</kbd></button>
      <button className="mi keys-only" role="menuitem" onClick={() => { close(); update({ modal: "help" }); }}><Icon name="kbd" size={16} />Keyboard shortcuts<kbd>?</kbd></button>
    </div>
  );
}

function Empty({ scope, label }: { scope: Scope; label: string }) {
  const filter = useStore((st) => st.filter);
  if (scope.type === "recent") return <div className="empty"><h2>Nothing read yet</h2><p>Articles you open show up here, most recent first, so one you opened by accident or want to find again is easy to get back to.</p></div>;
  if (scope.type === "search") return <div className="empty"><h2>No matches</h2><p>Search looks at titles and preview text.</p></div>;
  if (filter === "saved") return <div className="empty"><h2>Nothing saved{scope.type === "all" ? " yet" : ` in ${label}`}</h2><p>Press <kbd>S</kbd> on any article, or Save in the reader, to keep it here. Saved articles are never removed.</p></div>;
  if (scope.type === "topic") {
    return (
      <div className="empty calm">
        <span className="calm-mark" aria-hidden="true"><Icon name="hash" size={20} /></span>
        <h2>{filter === "all" ? `Nothing names “${label}” yet` : `Nothing unread names “${label}”`}</h2>
        <p>Articles from any source that name it, in the title or near the top, gather here as they arrive.</p>
        {filter === "unread" && <div className="acts2"><button className="btn ghost" onClick={() => setView(scope, "all")}>Show read articles</button></div>}
      </div>
    );
  }
  // read ones showing too and still nothing: say so, or "Show read articles" looks like it did nothing
  if (filter === "all") return <div className="empty"><h2>Nothing here yet</h2><p>No articles in {label} right now.</p></div>;
  return <CaughtUp scope={scope} label={label} />;
}

/** Nothing left to read: a quiet moment, when the sources were last looked at, and two ways onward. */
function CaughtUp({ scope, label }: { scope: Scope; label: string }) {
  const status = useOwnerQuery(api.feeds.status, {});
  const at = status?.lastCheckAt;
  const ago = at ? Math.max(0, Math.round((Date.now() - at) / 60_000)) : null;
  const next = ago === null ? null : Math.max(1, 30 - (ago % 30));
  return (
    <div className="empty calm">
      <span className="calm-mark" aria-hidden="true"><Icon name="check" size={20} /></span>
      <h2>You're all caught up</h2>
      <p>Everything in {label} is read.{ago !== null && <> Sources were checked {ago < 1 ? "just now" : `${ago} min ago`}; the next look is in about {next} min.</>}</p>
      <div className="acts2">
        <button className="btn ghost" onClick={() => setView(scope, "all")}>Show read articles</button>
        <button className="btn ghost" onClick={() => setView({ type: "recent" })}>Recently read</button>
      </div>
    </div>
  );
}

function Skeleton() {
  return <div className="skel" aria-hidden="true">{Array.from({ length: 8 }, (_, i) => <div key={i} className="sk-row"><i /><i /><i /></div>)}</div>;
}

/** Shown while rows are picked: how many, and the two things you can do to them. */
function PickBar({ shown }: { shown: number }) {
  const picked = useStore((st) => st.picks.picked);
  const { readMany } = useArticleActions();
  if (!picked.size) return null;
  const ids = [...picked];
  const rows = ids.map((id) => rowCache.get(id)).filter((r): r is Row => !!r).map(withOverlay);
  const anyUnread = rows.some((r) => !r.isRead), anyRead = rows.some((r) => r.isRead);
  const done = (v: boolean) => { readMany(ids, v); clearPicks(); };
  return (
    <div className="pickbar" role="toolbar" aria-label={`${ids.length} selected`}>
      <span className="pk-n">{ids.length} selected</span>
      {ids.length < shown && <button className="pk-all" onClick={pickAll} data-key="Ctrl A">Select all {shown}</button>}
      <span className="pk-sp" />
      {anyUnread && <button className="btn primary sm" onClick={() => done(true)}><Icon name="check" size={16} />Mark read</button>}
      {anyRead && <button className="btn sm" onClick={() => done(false)}><Icon name="circleDot" size={16} />Mark unread</button>}
      <button className="ib sm" onClick={clearPicks} aria-label="Clear selection" data-tip="Clear" data-key="Esc"><Icon name="x" size={16} /></button>
    </div>
  );
}
